// Floating page-anchor menu (mobile only).
//
// A small button fixed at the bottom-left opens a bottom sheet listing the
// sections / widgets on the *current* page. Tapping one smooth-scrolls to it.
// Mounted once at shell level (like the FAB) so it survives route changes —
// the section list is recomputed each time the sheet opens, so it always
// reflects whatever page is currently rendered in <main>.
//
// Detection is heuristic: we look for section/card headings inside <main> and
// scroll to each heading's nearest card/section container. Pages don't need to
// opt in, but a `data-anchor="Label"` attribute can override the label.

import { iconMarkup } from "@tkos/shared-ui";
import "./page-anchors.css";

const MAIN_ID = "tkos-main";

// Heading-ish selectors that mark the top of a widget across the various pages
// (CRM, dashboard, reports, payments, customer record, …). Broad on purpose.
const HEADING_SEL = [
  "[data-anchor]",
  "h1", "h2", "h3",
  ".cr-card-title",
  "[class*='__head']",
  "[class*='__title']",
  "[class*='-title']",
].join(",");

const SECTION_SEL = "section, article, [data-section], [class*='card'], [class*='__card']";

interface Anchor {
  label: string;
  target: HTMLElement;
}

function getMain(): HTMLElement | null {
  return document.getElementById(MAIN_ID);
}

function isVisible(el: HTMLElement): boolean {
  return el.offsetParent !== null || el.getClientRects().length > 0;
}

function collectAnchors(main: HTMLElement): Anchor[] {
  const seen = new Set<HTMLElement>();
  const out: Anchor[] = [];
  main.querySelectorAll<HTMLElement>(HEADING_SEL).forEach((h) => {
    if (!isVisible(h)) return;
    const label = (h.dataset["anchor"] || h.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!label || label.length > 60) return;
    const target = (h.closest(SECTION_SEL) as HTMLElement | null) ?? h;
    if (seen.has(target)) return;
    seen.add(target);
    out.push({ label, target });
  });
  return out.slice(0, 24);
}

function smoothScrollTo(target: HTMLElement): void {
  const offset = 64; // clear the fixed mobile topbar
  const main = getMain();
  if (main && main.scrollHeight > main.clientHeight + 4) {
    // <main> is the scroll container (desktop + most mobile layouts).
    const mRect = main.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    main.scrollTo({ top: main.scrollTop + (tRect.top - mRect.top) - offset, behavior: "smooth" });
  } else {
    // Body is the scroller — let the element handle it, with margin for the bar.
    target.style.scrollMarginTop = `${offset}px`;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export function mountPageAnchors(): () => void {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "tkos-anchor-fab";
  btn.setAttribute("aria-label", "Jump to a section on this page");
  btn.title = "Sections on this page";
  btn.innerHTML = iconMarkup("sort", { size: "md" });
  document.body.appendChild(btn);

  let sheet: HTMLElement | null = null;

  const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") close(); };

  function close(): void {
    if (sheet) { sheet.remove(); sheet = null; }
    document.removeEventListener("keydown", onKey);
  }

  function open(): void {
    close();
    const main = getMain();
    const anchors = main ? collectAnchors(main) : [];

    sheet = document.createElement("div");
    sheet.className = "tkos-anchor-overlay";
    const rows = anchors.length
      ? anchors
          .map(
            (a, i) =>
              `<button type="button" class="tkos-anchor-row" data-i="${i}">${escapeHtml(a.label)}</button>`,
          )
          .join("")
      : `<div class="tkos-anchor-empty">No sections on this page.</div>`;
    sheet.innerHTML = `
      <div class="tkos-anchor-sheet" role="dialog" aria-modal="true" aria-label="Page sections">
        <div class="tkos-anchor-sheet__grab" aria-hidden="true"></div>
        <div class="tkos-anchor-sheet__head">
          <span class="tkos-anchor-sheet__title">On this page</span>
          <button type="button" class="tkos-anchor-sheet__x" data-role="close" aria-label="Close">&times;</button>
        </div>
        <div class="tkos-anchor-sheet__list">${rows}</div>
      </div>`;
    sheet.addEventListener("mousedown", (e) => { if (e.target === sheet) close(); });
    sheet.querySelector<HTMLButtonElement>('[data-role="close"]')!.addEventListener("click", close);
    sheet.querySelectorAll<HTMLButtonElement>(".tkos-anchor-row").forEach((row) => {
      row.addEventListener("click", () => {
        const idx = Number(row.dataset["i"]);
        const a = anchors[idx];
        close();
        if (a) smoothScrollTo(a.target);
      });
    });
    document.addEventListener("keydown", onKey);
    document.body.appendChild(sheet);
  }

  btn.addEventListener("click", open);

  return () => { close(); btn.remove(); };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
}
